// v3.15-1206 COMP-013 — Chargeback Engine admin.
//
// Configuration-driven: plans + rules (create / edit / activate-deactivate / delete),
// trigger form, event log. Chargebacks are immutable compensating financial
// records — they flow into the Compensation Ledger as source_type="chargeback"
// with NEGATIVE amounts and settle through the existing Payroll Engine.
import { useCallback, useEffect, useState } from 'react';
import { Loader2, AlertTriangle, Plus, Play, Trash2, Pencil, Power, PowerOff, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Input } from '../components/ui/input';

const TRIGGER_TYPES     = ['deal_cancellation', 'refund', 'clawback', 'correction'];
const ADJUSTMENT_TYPES  = ['fixed', 'percentage'];
const REFERENCE_TYPES   = ['compensation_event', 'bonus_event', 'spiff_event', 'deal'];

const fmtMoney = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const fmtDate  = (iso) => (iso ? iso.slice(0, 10) : '—');

/* ============================== Plan modals ============================== */

const PlanModal = ({ open, onClose, onSaved, plan }) => {
  const editing = !!plan;
  const [companyId, setCompanyId] = useState(plan?.company_id || '');
  const [name, setName]           = useState(plan?.name || '');
  const [eff, setEff]             = useState(plan?.effective_date || new Date().toISOString().slice(0, 10));
  const [exp, setExp]             = useState(plan?.expiration_date || '');
  const [notes, setNotes]         = useState(plan?.notes || '');
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    setCompanyId(plan?.company_id || '');
    setName(plan?.name || '');
    setEff(plan?.effective_date || new Date().toISOString().slice(0, 10));
    setExp(plan?.expiration_date || '');
    setNotes(plan?.notes || '');
  }, [plan]);

  const submit = async () => {
    if (!editing && (!companyId || !name)) { toast.error('Company + Name required'); return; }
    if (editing && !name) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/chargeback-engine/plans/${plan.id}`, {
          name,
          effective_date:  eff  || undefined,
          expiration_date: exp  || undefined,
          notes:           notes || undefined,
        });
        toast.success('Plan updated');
      } else {
        await api.post('/chargeback-engine/plans', {
          company_id:      companyId,
          name,
          effective_date:  eff  || undefined,
          expiration_date: exp  || undefined,
          notes:           notes || undefined,
        });
        toast.success('Chargeback plan created');
      }
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <div className="p-2">
          <h2 className="text-xl font-extrabold text-slate-900 mb-3">
            {editing ? 'Edit Chargeback Plan' : 'New Chargeback Plan'}
          </h2>
          {!editing && (
            <>
              <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Company ID</label>
              <Input data-testid="cb-plan-company" value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="mb-3" placeholder="UUID" />
            </>
          )}
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Plan Name</label>
          <Input data-testid="cb-plan-name" value={name} onChange={(e) => setName(e.target.value)} className="mb-3" placeholder="Deal Cancellation Chargeback" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Effective Date</label>
          <Input data-testid="cb-plan-eff" type="date" value={eff} onChange={(e) => setEff(e.target.value)} className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Expiration Date</label>
          <Input data-testid="cb-plan-exp" type="date" value={exp} onChange={(e) => setExp(e.target.value)} className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Notes</label>
          <Input data-testid="cb-plan-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <div className="flex justify-end gap-2 mt-5">
            <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button data-testid="cb-plan-save" onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Save' : 'Create')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ============================== Rule modal ============================== */

const NewRuleModal = ({ open, onClose, onSaved, planId }) => {
  const [tt, setTt]         = useState('deal_cancellation');
  const [at, setAt]         = useState('percentage');
  const [fixed, setFixed]   = useState('');
  const [pct, setPct]       = useState('');
  const [eff, setEff]       = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes]   = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (at === 'fixed' && (!fixed || Number(fixed) <= 0))       { toast.error('Fixed amount > 0 required'); return; }
    if (at === 'percentage' && (!pct || Number(pct) <= 0))       { toast.error('Percentage > 0 required'); return; }
    setSaving(true);
    try {
      await api.post(`/chargeback-engine/plans/${planId}/rules`, {
        trigger_type:    tt,
        adjustment_type: at,
        fixed_amount:    at === 'fixed'      ? Number(fixed) : undefined,
        percentage:      at === 'percentage' ? Number(pct)   : undefined,
        effective_date:  eff,
        notes:           notes || undefined,
      });
      toast.success('Chargeback rule added'); onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <div className="p-2">
          <h2 className="text-xl font-extrabold text-slate-900 mb-3">Add Chargeback Rule</h2>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Trigger Type</label>
          <select data-testid="cb-rule-trigger" value={tt} onChange={(e) => setTt(e.target.value)}
                  className="w-full mb-3 border border-slate-300 rounded-lg px-3 py-2 text-sm">
            {TRIGGER_TYPES.map((t) => <option key={t} value={t}>{t.replaceAll('_', ' ')}</option>)}
          </select>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Adjustment Type</label>
          <select data-testid="cb-rule-adj" value={at} onChange={(e) => setAt(e.target.value)}
                  className="w-full mb-3 border border-slate-300 rounded-lg px-3 py-2 text-sm">
            {ADJUSTMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          {at === 'fixed' ? (
            <>
              <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Fixed Amount</label>
              <Input data-testid="cb-rule-fixed" type="number" step="0.01" value={fixed} onChange={(e) => setFixed(e.target.value)} className="mb-3" />
            </>
          ) : (
            <>
              <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Percentage (of reference amount)</label>
              <Input data-testid="cb-rule-pct" type="number" step="0.01" value={pct} onChange={(e) => setPct(e.target.value)} placeholder="100" className="mb-3" />
            </>
          )}
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Effective Date</label>
          <Input data-testid="cb-rule-eff" type="date" value={eff} onChange={(e) => setEff(e.target.value)} className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Notes</label>
          <Input data-testid="cb-rule-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <div className="flex justify-end gap-2 mt-5">
            <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button data-testid="cb-rule-save" onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Rule'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ============================== Trigger modal ============================== */

const TriggerModal = ({ open, onClose, onDone, plan }) => {
  const [ruleId, setRuleId]     = useState('');
  const [empId,  setEmpId]      = useState('');
  const [refType, setRefType]   = useState('compensation_event');
  const [refId,   setRefId]     = useState('');
  const [refAmt,  setRefAmt]    = useState('');
  const [bd,      setBd]        = useState(new Date().toISOString().slice(0, 10));
  const [note,    setNote]      = useState('');
  const [saving,  setSaving]    = useState(false);

  useEffect(() => { setRuleId(plan?.rules?.find(r => r.status === 'active')?.id || plan?.rules?.[0]?.id || ''); }, [plan]);

  if (!plan) return null;

  const run = async () => {
    if (!ruleId || !empId || !refId) { toast.error('Rule, employee, and reference required'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/chargeback-engine/trigger', {
        chargeback_plan_id: plan.id,
        chargeback_rule_id: ruleId,
        employee_id:        empId,
        reference_type:     refType,
        reference_id:       refId,
        reference_amount:   refAmt ? Number(refAmt) : undefined,
        business_date:      bd,
        note:               note || undefined,
      });
      toast.success(`Chargeback issued: ${fmtMoney(data.item.amount)}`);
      onDone?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Trigger failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <div className="p-2">
          <h2 className="text-xl font-extrabold text-slate-900 mb-3">Issue Chargeback — {plan.name}</h2>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Rule</label>
          <select data-testid="cb-trigger-rule" value={ruleId} onChange={(e) => setRuleId(e.target.value)}
                  className="w-full mb-3 border border-slate-300 rounded-lg px-3 py-2 text-sm">
            <option value="">— Select rule —</option>
            {(plan.rules || []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.trigger_type.replaceAll('_', ' ')} · {r.adjustment_type === 'fixed' ? fmtMoney(r.fixed_amount) : `${r.percentage}%`}
              </option>
            ))}
          </select>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Employee ID</label>
          <Input data-testid="cb-trigger-emp" value={empId} onChange={(e) => setEmpId(e.target.value)} placeholder="uuid" className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Reference Type</label>
          <select data-testid="cb-trigger-reftype" value={refType} onChange={(e) => setRefType(e.target.value)}
                  className="w-full mb-3 border border-slate-300 rounded-lg px-3 py-2 text-sm">
            {REFERENCE_TYPES.map((t) => <option key={t} value={t}>{t.replaceAll('_', ' ')}</option>)}
          </select>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Reference ID</label>
          <Input data-testid="cb-trigger-refid" value={refId} onChange={(e) => setRefId(e.target.value)} placeholder="uuid" className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Reference Amount (optional — resolved automatically when available)</label>
          <Input data-testid="cb-trigger-refamt" type="number" step="0.01" value={refAmt} onChange={(e) => setRefAmt(e.target.value)} className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Business Date</label>
          <Input data-testid="cb-trigger-date" type="date" value={bd} onChange={(e) => setBd(e.target.value)} className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Note</label>
          <Input data-testid="cb-trigger-note" value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="flex justify-end gap-2 mt-5">
            <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button data-testid="cb-trigger-run" onClick={run} disabled={saving} className="bg-rose-600 hover:bg-rose-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-4 h-4 mr-1" /> Issue</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ============================== Page ============================== */

export default function ChargebackEnginePage() {
  const [loading, setLoading] = useState(true);
  const [plans, setPlans]     = useState([]);
  const [events, setEvents]   = useState([]);
  const [tab, setTab]         = useState('plans');
  const [error, setError]     = useState(null);
  const [newPlan, setNewPlan] = useState(false);
  const [editPlan, setEditPlan]     = useState(null);
  const [newRuleFor, setNewRuleFor] = useState(null);
  const [triggerFor, setTriggerFor] = useState(null);
  const [replaying, setReplaying]   = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [p, e] = await Promise.all([
        api.get('/chargeback-engine/plans'),
        api.get('/chargeback-engine/events', { params: { limit: 500 } }),
      ]);
      setPlans(p?.data?.items || []);
      setEvents(e?.data?.items || []);
    } catch (err) {
      if (err?.response?.status === 403) setError('Chargeback administration is restricted to admins.');
      else toast.error('Failed to load chargeback data');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const togglePlan = async (p) => {
    const next = (p.status || 'active') === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/chargeback-engine/plans/${p.id}`, { status: next });
      toast.success(`Plan ${next === 'active' ? 'activated' : 'deactivated'}`); fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const delPlan = async (p) => {
    if (!window.confirm(`Delete chargeback plan "${p.name}"? This is irreversible.`)) return;
    try {
      await api.delete(`/chargeback-engine/plans/${p.id}`);
      toast.success('Plan deleted'); fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const toggleRule = async (planId, r) => {
    const next = (r.status || 'active') === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/chargeback-engine/plans/${planId}/rules/${r.id}`, { status: next });
      toast.success(`Rule ${next === 'active' ? 'activated' : 'deactivated'}`); fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const delRule = async (planId, ruleId) => {
    try {
      await api.delete(`/chargeback-engine/plans/${planId}/rules/${ruleId}`);
      toast.success('Rule removed'); fetchAll();
    } catch { toast.error('Delete failed'); }
  };

  const replay = async () => {
    setReplaying(true);
    try {
      const { data } = await api.post('/chargeback-engine/replay-projection');
      toast.success(`Replay complete — scanned ${data.scanned}, posted ${data.posted}, skipped ${data.skipped}`);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Replay failed'); }
    finally { setReplaying(false); }
  };

  return (
    <div className="space-y-4" data-testid="chargeback-engine-page">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-rose-600" />
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900">Chargeback Engine</h1>
            <p className="text-sm text-slate-500">
              Immutable compensating adjustments. Original earning records are
              never modified — every chargeback is an append-only negative
              record on the Compensation Ledger that settles through Payroll.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="cb-replay-btn" onClick={replay} disabled={replaying} variant="outline">
            {replaying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Replay Projection
          </Button>
          <Button data-testid="cb-new-plan-btn" onClick={() => setNewPlan(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white">
            <Plus className="w-4 h-4 mr-2" /> New Chargeback Plan
          </Button>
        </div>
      </div>

      {error ? (
        <div className="p-6 bg-rose-50 text-rose-700 border border-rose-200 rounded-xl">{error}</div>
      ) : loading ? (
        <div className="p-10 flex items-center justify-center text-slate-500 bg-white rounded-2xl border">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <>
          <div className="flex items-center gap-1 border-b border-slate-200">
            {['plans', 'events'].map((t) => (
              <button key={t} data-testid={`cb-tab-${t}`} onClick={() => setTab(t)}
                      className={
                        'px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ' +
                        (tab === t ? 'border-rose-600 text-rose-700' : 'border-transparent text-slate-500 hover:text-slate-800')
                      }>{t === 'plans' ? 'Plans & Rules' : 'Chargeback Events'}</button>
            ))}
          </div>

          {tab === 'plans' ? (
            plans.length === 0 ? (
              <div className="p-10 text-center text-slate-500 bg-white border rounded-2xl" data-testid="cb-plans-empty">
                No chargeback plans yet.
              </div>
            ) : (
              <div className="space-y-4">
                {plans.map((p) => {
                  const active = (p.status || 'active') === 'active';
                  return (
                    <div key={p.id} data-testid={`cb-plan-${p.id}`} className="bg-white rounded-2xl border border-slate-200 p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-bold text-slate-800">{p.name}</h3>
                            <span className={
                              'text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ' +
                              (active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600')
                            }>{p.status}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            Company {p.company_id?.slice(0, 8)}… · Effective {fmtDate(p.effective_date)}
                            {p.expiration_date ? ` · Expires ${fmtDate(p.expiration_date)}` : ''}
                          </p>
                          {p.notes && <p className="text-xs text-slate-500 mt-1">{p.notes}</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button size="sm" data-testid={`cb-edit-plan-${p.id}`} onClick={() => setEditPlan(p)} variant="outline">
                            <Pencil className="w-4 h-4 mr-1" /> Edit
                          </Button>
                          <Button size="sm" data-testid={`cb-toggle-plan-${p.id}`} onClick={() => togglePlan(p)} variant="outline">
                            {active ? <><PowerOff className="w-4 h-4 mr-1" /> Deactivate</> : <><Power className="w-4 h-4 mr-1" /> Activate</>}
                          </Button>
                          <Button size="sm" data-testid={`cb-add-rule-${p.id}`} onClick={() => setNewRuleFor(p.id)} variant="outline">
                            <Plus className="w-4 h-4 mr-1" /> Add Rule
                          </Button>
                          <Button size="sm" data-testid={`cb-trigger-${p.id}`} onClick={() => setTriggerFor(p)}
                                  disabled={!active || !(p.rules || []).some(r => (r.status || 'active') === 'active')}
                                  className="bg-rose-600 hover:bg-rose-700 text-white">
                            <Play className="w-4 h-4 mr-1" /> Issue
                          </Button>
                          <button data-testid={`cb-del-plan-${p.id}`} onClick={() => delPlan(p)}
                                  className="text-rose-500 hover:text-rose-700 p-2"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>

                      {(p.rules || []).length === 0 ? (
                        <div className="mt-3 text-xs text-slate-400">No rules yet.</div>
                      ) : (
                        <table className="w-full text-sm mt-3">
                          <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                            <tr>
                              <th className="text-left px-3 py-2">Trigger</th>
                              <th className="text-left px-3 py-2">Adjustment</th>
                              <th className="text-right px-3 py-2">Value</th>
                              <th className="text-left px-3 py-2">Effective</th>
                              <th className="text-center px-3 py-2">Status</th>
                              <th className="text-right px-3 py-2"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {p.rules.map((r) => {
                              const rActive = (r.status || 'active') === 'active';
                              return (
                                <tr key={r.id} data-testid={`cb-rule-${r.id}`} className="border-t border-slate-100">
                                  <td className="px-3 py-2 capitalize">{r.trigger_type.replaceAll('_', ' ')}</td>
                                  <td className="px-3 py-2 capitalize">{r.adjustment_type}</td>
                                  <td className="px-3 py-2 text-right font-semibold">
                                    {r.adjustment_type === 'fixed' ? fmtMoney(r.fixed_amount) : `${r.percentage}%`}
                                  </td>
                                  <td className="px-3 py-2 text-xs">{fmtDate(r.effective_date)}</td>
                                  <td className="px-3 py-2 text-center text-xs">
                                    <span className={
                                      'inline-block text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ' +
                                      (rActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600')
                                    }>{r.status}</span>
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    <div className="flex items-center justify-end gap-1">
                                      <button data-testid={`cb-toggle-rule-${r.id}`} onClick={() => toggleRule(p.id, r)}
                                              className="text-slate-500 hover:text-slate-800 p-1"
                                              title={rActive ? 'Deactivate' : 'Activate'}>
                                        {rActive ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                                      </button>
                                      <button data-testid={`cb-del-rule-${r.id}`} onClick={() => delRule(p.id, r.id)}
                                              className="text-rose-500 hover:text-rose-700 p-1" title="Delete rule">
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto">
              {events.length === 0 ? (
                <div className="p-10 text-center text-slate-500" data-testid="cb-events-empty">No chargeback events yet.</div>
              ) : (
                <table className="w-full text-sm" data-testid="cb-events-table">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                    <tr>
                      <th className="text-left px-3 py-2">Date</th>
                      <th className="text-left px-3 py-2">Employee</th>
                      <th className="text-left px-3 py-2">Plan</th>
                      <th className="text-left px-3 py-2">Trigger</th>
                      <th className="text-left px-3 py-2">Reference</th>
                      <th className="text-right px-3 py-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((e) => (
                      <tr key={e.id} className="border-t border-slate-100">
                        <td className="px-3 py-2 whitespace-nowrap">{fmtDate(e.business_date)}</td>
                        <td className="px-3 py-2">{e.employee_name || e.employee_id}</td>
                        <td className="px-3 py-2">{e.chargeback_plan_name}</td>
                        <td className="px-3 py-2 capitalize">{(e.trigger_type || '').replaceAll('_', ' ')}</td>
                        <td className="px-3 py-2 text-xs">
                          {(e.reference_type || '').replaceAll('_', ' ')} · {(e.reference_id || '').slice(0, 8)}…
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-rose-700">{fmtMoney(e.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}

      <PlanModal open={newPlan}  onClose={() => setNewPlan(false)}  onSaved={fetchAll} plan={null} />
      <PlanModal open={!!editPlan} onClose={() => setEditPlan(null)} onSaved={fetchAll} plan={editPlan} />
      <NewRuleModal open={!!newRuleFor} onClose={() => setNewRuleFor(null)} onSaved={fetchAll} planId={newRuleFor} />
      <TriggerModal open={!!triggerFor} onClose={() => setTriggerFor(null)} onDone={fetchAll} plan={triggerFor} />
    </div>
  );
}
